import { useEffect, useRef, useCallback } from 'react';
import api from '../../api/client';

/**
 * Deck-delivery telemetry (fire-and-forget).
 *
 * Records a usage event when a trainer PRESENTS a module in fullscreen/
 * projector mode — start on enter, finalize (duration + distinct slides shown)
 * on exit / unmount / tab close. This is the "actual usage" signal surfaced in
 * Settings → Activity Logs → Usage.
 *
 * Every call is best-effort: telemetry must NEVER disrupt the presentation, so
 * failures are swallowed. On teardown we prefer navigator.sendBeacon (survives
 * a hard tab close); auth rides on the httpOnly session cookie since the API is
 * same-origin.
 */
export function useDeckUsageTracker({ presenting, moduleId, moduleTitle, currentIndex, totalSlides }) {
  const usageIdRef = useRef(null);
  const visitedRef = useRef(new Set());
  const activeRef = useRef(false);
  const metaRef = useRef({ moduleId, moduleTitle, totalSlides, currentIndex });

  // Keep latest meta + count distinct slides shown (ref writes happen inside an
  // effect, never during render).
  useEffect(() => {
    metaRef.current = { moduleId, moduleTitle, totalSlides, currentIndex };
    if (activeRef.current && typeof currentIndex === 'number') {
      visitedRef.current.add(currentIndex);
    }
  }, [moduleId, moduleTitle, totalSlides, currentIndex]);

  const finalize = useCallback(() => {
    const id = usageIdRef.current;
    if (!id) return;
    usageIdRef.current = null;
    const body = { slides_viewed: visitedRef.current.size };
    try {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      const ok = navigator.sendBeacon?.(`/api/v1/usage/deck-presentations/${id}/end`, blob);
      if (!ok) api.post(`/usage/deck-presentations/${id}/end`, body).catch(() => {});
    } catch {
      api.post(`/usage/deck-presentations/${id}/end`, body).catch(() => {});
    }
  }, []);

  // Start / end on presenting transitions.
  useEffect(() => {
    let cancelled = false;
    if (presenting && !activeRef.current && metaRef.current.moduleId) {
      activeRef.current = true;
      const { moduleId: mId, moduleTitle: mTitle, totalSlides: tSlides, currentIndex: idx } = metaRef.current;
      visitedRef.current = new Set(typeof idx === 'number' ? [idx] : []);
      api.post('/usage/deck-presentations/start', {
        module_id: mId,
        module_title: mTitle || undefined,
        slides_total: typeof tSlides === 'number' ? tSlides : undefined,
      })
        .then((res) => { if (!cancelled) usageIdRef.current = res.data?.usage_id || null; })
        .catch(() => {});
    } else if (!presenting && activeRef.current) {
      activeRef.current = false;
      finalize();
    }
    return () => { cancelled = true; };
  }, [presenting, finalize]);

  // Hard tab-close + unmount safety net.
  useEffect(() => {
    const onHide = () => { if (activeRef.current) finalize(); };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      if (activeRef.current) finalize();
    };
  }, [finalize]);
}
